import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'

import { CardReconcileStatus } from '@ankor-io/common/types/cards'
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogDescription,
  DialogTitle,
  Loading,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ankor-io/ui-components'

import {
  useBackfillConnectedAccountCardMutation,
  useReconcileConnectedAccountCardStatusMutation,
  useReconcileConnectedAccountCardsQuery,
} from '@/queries'

const STATUS_BADGE: Record<CardReconcileStatus, { color: 'amber' | 'green' | 'red'; label: string }> = {
  // Actionable anomaly — present in Airwallex but missing from our database.
  [CardReconcileStatus.AWX_ONLY]: { color: 'amber', label: 'AWX only' },
  // Healthy — present in both systems.
  [CardReconcileStatus.IN_BOTH]: { color: 'green', label: 'In both' },
  // Present in both, but the statuses disagree — the card could be usable in one system and not the other.
  [CardReconcileStatus.STATUS_MISMATCH]: { color: 'red', label: 'Status mismatch' },
}

// STATUS_MISMATCH sorts first: a card usable in one system but not the other is the most dangerous anomaly.
const STATUS_SORT: Record<CardReconcileStatus, number> = {
  [CardReconcileStatus.STATUS_MISMATCH]: 0,
  [CardReconcileStatus.AWX_ONLY]: 1,
  [CardReconcileStatus.IN_BOTH]: 2,
}

export function ConnectedAccountCardsReconcile({
  connectedAccountId,
  orgId,
  vesselId,
}: {
  connectedAccountId: string
  orgId: string
  vesselId: string
}) {
  const { data, isFetching, isError, error, refetch } = useReconcileConnectedAccountCardsQuery(
    orgId,
    vesselId,
    connectedAccountId,
  )
  const backfill = useBackfillConnectedAccountCardMutation()
  const reconcileStatus = useReconcileConnectedAccountCardStatusMutation()
  const [confirmCardId, setConfirmCardId] = useState<string | null>(null)
  const [reconcileCardId, setReconcileCardId] = useState<string | null>(null)

  const rows = useMemo(
    () => (data ? [...data].sort((a, b) => STATUS_SORT[a.status] - STATUS_SORT[b.status]) : []),
    [data],
  )

  const handleBackfill = () => {
    if (!confirmCardId) {
      return
    }
    const runBackfill = async () => {
      await backfill.mutateAsync({ orgId, vesselId, connectedAccountId, cardId: confirmCardId })
      setConfirmCardId(null)
      // Re-run the reconcile so the backfilled card flips to "In both".
      refetch()
    }
    toast.promise(runBackfill, {
      loading: 'Backfilling card…',
      success: 'Card backfilled successfully.',
      error: 'Failed to backfill card.',
    })
  }

  const handleReconcileStatus = () => {
    if (!reconcileCardId) {
      return
    }
    const runReconcile = async () => {
      await reconcileStatus.mutateAsync({ orgId, vesselId, connectedAccountId, cardId: reconcileCardId })
      setReconcileCardId(null)
      // Re-run the reconcile so the corrected card flips to "In both".
      refetch()
    }
    toast.promise(runReconcile, {
      loading: 'Reconciling status…',
      success: 'Card status reconciled successfully.',
      error: 'Failed to reconcile card status.',
    })
  }

  return (
    <div className="mt-8">
      <h4 className="font-bold text-sm">Reconcile cards (last 90 days)</h4>
      <p className="text-sm text-gray-500 mt-0.5 dark:text-gray-400">
        Compares Airwallex cards created in the last 90 days against AnkorPay. Use the card checker above for older
        cards.
      </p>

      <div className="mt-3">
        {data ? (
          <Button
            plain
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Re-run reconciliation
          </Button>
        ) : (
          <Button
            color="primary"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Run reconciliation
          </Button>
        )}
      </div>

      {isFetching && (
        <div className="flex items-center gap-2 mt-3 text-sm text-gray-500">
          <Loading />
          <span>Fetching cards from Airwallex…</span>
        </div>
      )}

      {isError && !isFetching && (
        <p className="mt-3 text-sm text-red-600">
          Failed to fetch Airwallex cards. {error instanceof Error ? error.message : ''} Try again or use the card
          checker for individual lookups.
        </p>
      )}

      {!isFetching && !isError && data?.length === 0 && (
        <p className="mt-3 text-sm text-gray-500">No Airwallex cards found for the last 90 days.</p>
      )}

      {!isFetching && !isError && rows.length > 0 && (
        <div className="overflow-x-auto mt-4">
          <Table className="max-w-[calc(100vw-2rem)] lg:max-w-[calc(100vw-20rem)]">
            <TableHead>
              <TableRow>
                <TableHeader>Status</TableHeader>
                <TableHeader>Card ID</TableHeader>
                <TableHeader>Last 4 digits</TableHeader>
                <TableHeader>Airwallex status</TableHeader>
                <TableHeader>AnkorPay status</TableHeader>
                <TableHeader>Cardholder Name</TableHeader>
                <TableHeader>Card Nickname</TableHeader>
                <TableHeader>Action</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.cardId}>
                  <TableCell>
                    <Badge color={STATUS_BADGE[row.status].color}>{STATUS_BADGE[row.status].label}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-gray-900 dark:text-gray-100">{row.cardId}</TableCell>
                  <TableCell className="text-sm text-gray-900 dark:text-gray-100">{row.maskedCardNumber}</TableCell>
                  <TableCell className="text-sm text-gray-900 dark:text-gray-100">{row.cardStatus}</TableCell>
                  <TableCell
                    className={
                      row.status === CardReconcileStatus.STATUS_MISMATCH
                        ? 'text-sm font-medium text-red-700 dark:text-red-400'
                        : 'text-sm text-gray-900 dark:text-gray-100'
                    }
                  >
                    {row.dbStatus ?? '—'}
                  </TableCell>
                  <TableCell className="text-sm text-gray-900 dark:text-gray-100">
                    {row.cardholderName || '—'}
                  </TableCell>
                  <TableCell className="text-sm text-gray-900 dark:text-gray-100">{row.nickName || '—'}</TableCell>
                  <TableCell>
                    {row.status === CardReconcileStatus.AWX_ONLY &&
                      (backfill.isPending && confirmCardId === row.cardId ? (
                        <Loading />
                      ) : (
                        <Button
                          plain
                          onClick={() => setConfirmCardId(row.cardId)}
                        >
                          Backfill
                        </Button>
                      ))}
                    {row.status === CardReconcileStatus.STATUS_MISMATCH &&
                      (reconcileStatus.isPending && reconcileCardId === row.cardId ? (
                        <Loading />
                      ) : (
                        <Button
                          plain
                          onClick={() => setReconcileCardId(row.cardId)}
                        >
                          Reconcile status
                        </Button>
                      ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog
        open={confirmCardId !== null}
        onClose={() => setConfirmCardId(null)}
        size="md"
      >
        <DialogTitle>Backfill card</DialogTitle>
        <DialogDescription>
          This will create a record for card <span className="font-medium">{confirmCardId}</span> in the AnkorPay
          database.
        </DialogDescription>
        <DialogActions>
          <Button
            plain
            className="disabled:cursor-not-allowed"
            onClick={() => setConfirmCardId(null)}
            disabled={backfill.isPending}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            className="disabled:cursor-not-allowed"
            onClick={handleBackfill}
            disabled={backfill.isPending}
          >
            {backfill.isPending ? 'Backfilling…' : 'Backfill'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reconcileCardId !== null}
        onClose={() => setReconcileCardId(null)}
        size="md"
      >
        <DialogTitle>Reconcile card status</DialogTitle>
        <DialogDescription>
          This will update the AnkorPay status for card <span className="font-medium">{reconcileCardId}</span> to match
          Airwallex.
        </DialogDescription>
        <DialogActions>
          <Button
            plain
            className="disabled:cursor-not-allowed"
            onClick={() => setReconcileCardId(null)}
            disabled={reconcileStatus.isPending}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            className="disabled:cursor-not-allowed"
            onClick={handleReconcileStatus}
            disabled={reconcileStatus.isPending}
          >
            {reconcileStatus.isPending ? 'Reconciling…' : 'Reconcile status'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
